import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    Box, 
    Typography, 
    TextField, 
    Button,  
    CircularProgress, 
    FormControlLabel, 
    Checkbox, 
} from '@mui/material';
import {
    // Add,
    // Delete,
    ArrowBackIosNew,
} from '@mui/icons-material';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import CustomDatePicker from "components/CustomDatePicker";
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const AirportPairPage = () => {
    useAuthCheck();
    const { airportPairId } = useParams();    
    const navigate = useNavigate();
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [airportPair, setAirportPair] = useState({});
    const initialValues = {
        sector: airportPair.sector || '',    
        sectorType: airportPair.sectorType || '',      
        routeName: airportPair.routeName || '',        
        blockHours: airportPair.blockHours || 0,
        distance: airportPair.distance || 0,
        startDate: airportPair.startDate ? new Date(airportPair.startDate) : new Date(),
        endDate: airportPair.endDate ? new Date(airportPair.endDate) : new Date(),
        description: airportPair.description || '',        
        isActive: airportPair.isActive ?? true,
    };
        
    
    useEffect(() => {        
        const fetchAirportPair = async () => {
            try {
                setError(null);
                setLoading(true);       

                if (airportPairId) {
                    const url = `/airportpairs/${airportPairId}`;                    
                    const response = await api.get(url);
        
                    setAirportPair(response.data); 
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchAirportPair();
    }, [airportPairId]);
    
    const checkoutSchema = yup.object().shape({
        sector: yup.string().required('Required'),
    });

    const handleFormSubmit = useCallback(async (values, onSubmitProps) => {
        try {            
            const method = airportPairId ? 'put' : 'post';    
            const url = airportPairId
            ? `/airportpairs/update/${airportPairId}`
            : '/airportpairs/new';    

            const dateFields = [                
                'startDate',
                'endDate',
            ];
            const payload = {
                ...values,
                ...dateFields.reduce((acc, field) => {
                    acc[field] = values[field] ? values[field].toISOString() : null;
                    return acc;
                }, {}),
            };
            
            await api({
                method, 
                url,
                data: payload 
            });
    
            toast.success(airportPairId
                ? 'Airport pair updated successfully.'
                : 'Airport pair created successfully.'
            );
    
            onSubmitProps.resetForm();
            navigate('/airportpairs');
        } catch (error) {
            setError(error);
            toast.error(error.message || 'Failed to submit the form!');
        }
    }, [navigate, airportPairId]);

    
    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Airport Pair"
                    subtitle={`${loading ? 'Loading...' : airportPairId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/airportpairs")}
                        >
                            <ArrowBackIosNew />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <LocalizationProvider 
                        dateAdapter={AdapterDateFns}
                        adapterLocale={locales['en-gb']}
                    >  
                        <Formik
                            initialValues={initialValues}
                            enableReinitialize={true}
                            validationSchema={checkoutSchema}
                            onSubmit={handleFormSubmit}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                setFieldValue,
                            }) => (
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        display="grid"
                                        gap={2}
                                        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    >
                                        <TextField
                                            label="Sector"
                                            name="sector"
                                            required
                                            variant="filled"
                                            type="text"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.sector}
                                            error={!!touched.sector && !!errors.sector}
                                            helperText={touched.sector && errors.sector}
                                            sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                        />

                                        <TextField
                                            label="Route Name"
                                            name="routeName"
                                            variant="filled"
                                            type="text"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.routeName}
                                            error={!!touched.routeName && !!errors.routeName}
                                            helperText={touched.routeName && errors.routeName}
                                            sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                        />

                                        <TextField
                                            label="Block Hours"
                                            name="blockHours"
                                            variant="filled"
                                            type="number"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.blockHours}
                                            error={!!touched.blockHours && !!errors.blockHours}
                                            helperText={touched.blockHours && errors.blockHours}
                                            sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                        />

                                        <TextField
                                            label="Distance"
                                            name="distance"
                                            variant="filled"
                                            type="number"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.distance}
                                            error={!!touched.distance && !!errors.distance}
                                            helperText={touched.distance && errors.distance}
                                            sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                        />

                                        <CustomDatePicker
                                            label="Start Date"
                                            name="startDate"
                                            value={values.startDate}
                                            maxDate={values.endDate}
                                            onChange={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}
                                        />

                                        <CustomDatePicker
                                            label="End Date"
                                            name="endDate"
                                            value={values.endDate}
                                            minDate={values.startDate}
                                            onChange={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}
                                        />
                                        
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            multiline
                                            rows={3}
                                            label="Description"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.description}
                                            name="description"
                                            error={!!touched.description && !!errors.description}
                                            helperText={touched.description && errors.description}
                                            sx={{ gridColumn: 'span 12' }}
                                        />
                                        
                                        <TextField
                                            label="Sector Type"
                                            name="sectorType"
                                            variant="filled"
                                            type="text"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.sectorType}
                                            error={!!touched.sectorType && !!errors.sectorType}
                                            helperText={touched.sectorType && errors.sectorType}
                                            sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                        />

                                        <FormControlLabel
                                            sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                            control={
                                                <Checkbox
                                                    checked={values.isActive}
                                                    onChange={handleChange}
                                                    name="isActive"
                                                    color="primary"
                                                />
                                            }
                                            label="Is Active"
                                        />
                                        
                                        <Button
                                            fullWidth
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            sx={{ gridColumn: "span 12", height: 42 }}
                                        >
                                            {airportPairId ? "Update" : "Create"}
                                        </Button>
                                    </Box>
                                </form>
                            )}
                        </Formik>
                    </LocalizationProvider>
                </Box>
            )}
        </Box>
    );
};

export default AirportPairPage;
